import React, { useEffect, useState, useRef, useContext } from 'react';
import useLayoutEffect from './useIsomorphicLayoutEffect';
import FormGroupControl from '../controllers/FormGroupControl';
import useIsEqualMemoize from './useIsEqualMemoize';
import createRef from '../utils/createRef';
import createCustomSchema from '../utils/createCustomSchema';
import { FormGroupContext, FormContext } from '../Context';

export default function useFormGroupControl({
  $ref: userRef,
  defaultValues,
  fields: userFields,
  initialValues,
  name,
  property,
  schema,
  validateFields,
  ...userProps
}) {
  const parentRef = useContext(FormGroupContext);
  const context = useContext(FormContext);
  const isMounted = useRef(false);

  const [formGroupControl] = useState(() =>
    new FormGroupControl({
      ...userProps,
      defaultValues,
      initialValues,
      schema,
    }).withContext(
      {
        ...context,
        ...context.stateRef.current,
      },
      createRef(userRef ?? property ?? name, parentRef),
      createCustomSchema(userFields)
    )
  );

  const isEqualSchema = useIsEqualMemoize(schema);
  const isEqualDefaultValues = useIsEqualMemoize(defaultValues);
  const isEqualInitialValues = useIsEqualMemoize(initialValues);

  useEffect(() => {
    formGroupControl.setProps({
      ...userProps,
      defaultValues,
      initialValues,
      schema,
    });
  }, [useIsEqualMemoize(userProps), isEqualSchema, isEqualDefaultValues, isEqualInitialValues]);

  const afterMount = (fn) => {
    if (!isMounted.current) {
      return () => {};
    }

    return fn;
  };

  useEffect(
    afterMount(() => {
      formGroupControl.updateUserSchema(schema);
    }),
    [isEqualSchema]
  );

  useEffect(
    afterMount(() => {
      formGroupControl.updateDefaultValues(defaultValues);
    }),
    [isEqualDefaultValues]
  );

  useEffect(
    afterMount(() => {
      formGroupControl.updateInitialValues(initialValues);
    }),
    [isEqualInitialValues]
  );

  const {
    $ref,
    getProps,
    actions,
    properties,
    fields,
    subscribe,
    subscribeContext,
    unsubscribe,
    unsubscribeContext,
  } = formGroupControl;

  const [formGroupContext, setFormGroupContext] = useState({
    ...actions,
    ...properties,
  });

  const updateHandler = () => {
    if (!isMounted.current) {
      return;
    }

    formGroupControl.withContext(
      {
        ...context,
        ...context.stateRef.current,
      },
      $ref,
      createCustomSchema(userFields)
    );

    setFormGroupContext({
      ...formGroupControl.actions,
      ...formGroupControl.properties,
    });
  };

  useEffect(updateHandler, [userRef, name, property]);

  // subscribe to changes
  useEffect(() => {
    subscribeContext();

    formGroupControl.subscribe('change', updateHandler);
    formGroupControl.subscribe('invalid', updateHandler);
    formGroupControl.subscribe('schema', updateHandler);
    formGroupControl.subscribe('valid', updateHandler);

    isMounted.current = true;

    return () => {
      unsubscribeContext();

      isMounted.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (validateFields) subscribe('validateFields', validateFields);

    return () => {
      if (validateFields) unsubscribe('validateFields', validateFields);
    };
  }, [validateFields]);

  const render = (children) => (
    <FormGroupContext.Provider value={$ref}>{children}</FormGroupContext.Provider>
  );

  return {
    fields,
    formGroupContext,
    formGroupProps: getProps(),
    render,
    userProps,
  };
}
